import { NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { FlexLayoutModule } from '@angular/flex-layout';
import {
  DateTimeAdapter,
  OWL_DATE_TIME_FORMATS,
  OWL_DATE_TIME_LOCALE,
  OwlDateTimeModule,
  OwlNativeDateTimeModule,
} from '@danielmoncada/angular-datetime-picker';
import { BUTTONS, CUSTOM_LIBRARY_BUTTONS, NgxWigModule } from 'ngx-wig';
import { PICK_DATETIME_CUSTOM_DATE_FORMAT, PickDatetimeDateAdapter } from './pick-datetime-date-adapter';
import { UserInfoComponent } from './components/user-info/user-info.component';
import { FormFieldAbstractComponent } from './form-fields/components/abstract.component';
import { FormFieldTextComponent } from './form-fields/components/text/text.component';
import { FormFieldDatetimeIntervalComponent } from './form-fields/components/datetime-interval/datetime-interval.component';
import { FormFieldCheckboxComponent } from './form-fields/components/checkbox/checkbox.component';
import { FormFieldSelectComponent } from './form-fields/components/select/select.component';
import { FormFieldNumberComponent } from './form-fields/components/number/number.component';
import { FormFieldAutocompleteComponent } from './form-fields/components/autocomplete/autocomplete.component';
import { FormFieldUniversalComponent } from './form-fields/components/universal/universal.component';
import { FormFieldRadioButtonComponent } from './form-fields/components/radio-button/radio-button.component';
import { ConfirmDialogComponent } from './components/dialogs/confirm-dialog.component';
import {
  ConstantPipe,
  CurrentTimePipe,
  FullPathTransPipe,
  HasPermissionPipe,
  IsCrm1EducationServicePipe,
  LocalizedDatePipe,
  ProductConfigPipe,
  SafeIframeHtmlPipe,
  UtcOffsetToTimePipe,
  GetMskUtcPipe,
  LocalToTimezonePipe,
} from '@shared/shared/pipes';
import { FormFieldDateIntervalComponent } from './form-fields/components/date-interval/date-interval.component';
import { FormFieldTextareaComponent } from './form-fields/components/textarea/textarea.component';
import { NgxErrorsModule } from '@skyeng/libs/shared/ngxerrors';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { FormFieldDateComponent } from './form-fields/components/date/date.component';
import { RowComponent } from './components/row/row.component';
import { LoadingProgressComponent } from './components/loading-progress/loading-progress.component';
import { SubmitOnHotkeyDirective } from './directives/submit-on-hotkey.directive';
import { TrimInputDirective } from './directives/trim-input.directive';
import { TitleComponent } from './components/title/title.component';
import { PriceTableComponent } from './components/price-table/price-table.component';
import { FormFieldFileComponent } from './form-fields/components/file/file.component';
import { FormFieldWysiwygComponent } from './form-fields/components/wysiwyg/wysiwyg.component';
import { NgxWigMatInputComponent } from './form-fields/components/wysiwyg/ngx-wig-mat-input.component';
import { BadgeComponent } from './components/badge/badge.component';
import { NoContentComponent } from './components/no-content/no-content.component';
import { FormFieldScheduleComponent } from './form-fields/components/schedule/schedule.component';
import { ScheduleMatInputComponent } from './form-fields/components/schedule/schedule-mat-input.component';
import { OverbookingGridMatInputComponent } from './form-fields/components/overbooking-grid/overbooking-grid-mat-input.component';
import { FormFieldOverbookingGridComponent } from './form-fields/components/overbooking-grid/overbooking-grid.component';
import { PromptDialogComponent } from './components/dialogs/prompt-dialog.component';
import { ContainerComponent } from './components/container/container.component';
import { ActionsRowComponent } from './components/actions-row/actions-row.component';
import { GridComponent } from './components/grid/grid/grid.component';
import { GridColComponent } from './components/grid/col/grid-col.component';
import { MailComponent } from './components/mail/mail/mail.component';
import { MailMessageSnippetComponent } from './components/mail/message-snippet/mail-message-snippet.component';
import { MailParticipantFromPipe } from './components/mail/pipes/mail-participant-from.pipe';
import { MailThreadComponent } from './components/mail/thread/mail-thread.component';
import { MailMessageComponent } from './components/mail/message/mail-message.component';
import { FileIconPipe } from './components/mail/pipes/file-icon.pipe';
import { MailReplyComponent } from './components/mail/reply/mail-reply.component';
import { LinkifyPipe } from './pipes/linkify.pipe';
import { ButtonLoadingDirective } from './directives/button-loading.directive';
import { InfoDialogComponent } from '@shared/shared/components/dialogs/info-dialog.component';
import { TrackUseCaseDirective } from './directives/track-use-case.directive';
import { InitVarDirective } from '@shared/shared/directives/init-var.directive';
import { CrmScheduleTimePipe } from '@shared/shared/form-fields/components/schedule/schedule-time.pipe';
import { SkeletonDirective } from '@shared/shared/directives/skeleton.directive';
import { FormFieldCheckboxSliderComponent } from '@shared/shared/form-fields/components/checkbox-slider/checkbox-slider.component';
import { SearchFieldComponent } from '@shared/shared/components/search-field/search-field.component';
import { TranslocoModule, TranslocoService } from '@ngneat/transloco';
import { registerTranslationsManually } from '@skyeng/libs/shared/i18n';
import { I18nCommonModule } from '@shared/i18n-common';
import { GetTimeZoneTimePipe } from '@shared/shared/pipes/get-time-zone-time.pipe';
import { TextFieldModule } from '@angular/cdk/text-field';
import { VimCoreWidgetsPlatformModule } from '@skyeng/libs/vimbox-core/widgets/platform';
import { en_US, NZ_I18N } from 'ng-zorro-antd/i18n';
import { NzPaginationModule } from 'ng-zorro-antd/pagination';
import { StkTitlePipe } from './pipes/stk-title.pipe';
import { MaterialModule } from './material.module';
import { LocalDatePickerComponent } from '@shared/shared/form-fields/components/local-date-picker/local-date-picker.component';
import { SkyUiModule } from '@skyeng/libs/shared/ui-kit';
import { UtcToTimezonePipe } from '@shared/shared/pipes/utc-to-timezone.pipe';
import { LocalMoscowTimezoneComponent } from '@shared/shared/components/local-moscow-timezone/local-moscow-timezone.component';
import { DropdownDirective } from './directives/dropdown.directive';
import { OverlayModule } from '@angular/cdk/overlay';
import { DsTooltipModule } from '@skyeng/libs/shared/ui-kit-v2/tooltip';

// import {GetMskUtcPipe} from "@shared/shared/pipes/get-msk-utc.pipe";

// do not display redundant text after page size in nz-paginator
const CUSTOM_LANGUAGE_CONFIG = {
  en_US,
  ...{
    Pagination: {
      items_per_page: '',
    },
  },
};

@NgModule({
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    OverlayModule,
    MaterialModule,
    FlexLayoutModule,
    NgxErrorsModule,
    OwlDateTimeModule,
    OwlNativeDateTimeModule,
    NgxWigModule,
    NgxMatSelectSearchModule,
    TranslocoModule,
    I18nCommonModule,
    TextFieldModule,
    VimCoreWidgetsPlatformModule,
    NzPaginationModule,
    SkyUiModule,
    DsTooltipModule,
  ],
  exports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MaterialModule,
    FlexLayoutModule,
    NgxErrorsModule,
    NgxMatSelectSearchModule,
    OwlDateTimeModule,
    OwlNativeDateTimeModule,
    FormFieldAbstractComponent,
    FormFieldTextComponent,
    FormFieldTextareaComponent,
    FormFieldDateComponent,
    FormFieldDateIntervalComponent,
    FormFieldDatetimeIntervalComponent,
    FormFieldCheckboxComponent,
    FormFieldScheduleComponent,
    FormFieldOverbookingGridComponent,
    FormFieldSelectComponent,
    FormFieldNumberComponent,
    FormFieldAutocompleteComponent,
    FormFieldUniversalComponent,
    FormFieldRadioButtonComponent,
    FormFieldFileComponent,
    FormFieldWysiwygComponent,
    UserInfoComponent,
    ConfirmDialogComponent,
    ConstantPipe,
    SafeIframeHtmlPipe,
    CurrentTimePipe,
    RowComponent,
    LoadingProgressComponent,
    SubmitOnHotkeyDirective,
    TrimInputDirective,
    TitleComponent,
    PriceTableComponent,
    UtcOffsetToTimePipe,
    IsCrm1EducationServicePipe,
    BadgeComponent,
    NoContentComponent,
    HasPermissionPipe,
    PromptDialogComponent,
    ContainerComponent,
    ActionsRowComponent,
    GridComponent,
    GridColComponent,
    MailComponent,
    LinkifyPipe,
    ButtonLoadingDirective,
    FileIconPipe,
    TrackUseCaseDirective,
    InitVarDirective,
    CrmScheduleTimePipe,
    ProductConfigPipe,
    SkeletonDirective,
    SearchFieldComponent,
    TranslocoModule,
    FullPathTransPipe,
    VimCoreWidgetsPlatformModule,
    LocalizedDatePipe,
    NzPaginationModule,
    GetMskUtcPipe,
    GetTimeZoneTimePipe,
    LocalDatePickerComponent,
    LocalToTimezonePipe,
    UtcToTimezonePipe,
    LocalMoscowTimezoneComponent,
    DropdownDirective,
    StkTitlePipe,
  ],
  declarations: [
    FormFieldAbstractComponent,
    FormFieldTextComponent,
    FormFieldTextareaComponent,
    FormFieldDateIntervalComponent,
    FormFieldDateComponent,
    FormFieldDatetimeIntervalComponent,
    FormFieldCheckboxComponent,
    FormFieldSelectComponent,
    FormFieldNumberComponent,
    FormFieldAutocompleteComponent,
    FormFieldUniversalComponent,
    FormFieldRadioButtonComponent,
    FormFieldFileComponent,
    FormFieldCheckboxSliderComponent,
    NgxWigMatInputComponent,
    FormFieldWysiwygComponent,
    UserInfoComponent,
    ConfirmDialogComponent,
    InfoDialogComponent,
    ConstantPipe,
    SafeIframeHtmlPipe,
    CurrentTimePipe,
    RowComponent,
    LoadingProgressComponent,
    SubmitOnHotkeyDirective,
    TrimInputDirective,
    TitleComponent,
    PriceTableComponent,
    UtcOffsetToTimePipe,
    IsCrm1EducationServicePipe,
    BadgeComponent,
    NoContentComponent,
    ScheduleMatInputComponent,
    OverbookingGridMatInputComponent,
    FormFieldScheduleComponent,
    FormFieldOverbookingGridComponent,
    HasPermissionPipe,
    PromptDialogComponent,
    ContainerComponent,
    ActionsRowComponent,
    GridComponent,
    GridColComponent,
    MailComponent,
    MailMessageSnippetComponent,
    MailParticipantFromPipe,
    MailThreadComponent,
    MailMessageComponent,
    FileIconPipe,
    MailReplyComponent,
    SearchFieldComponent,
    LinkifyPipe,
    ButtonLoadingDirective,
    TrackUseCaseDirective,
    InitVarDirective,
    CrmScheduleTimePipe,
    ProductConfigPipe,
    SkeletonDirective,
    FullPathTransPipe,
    GetTimeZoneTimePipe,
    LocalizedDatePipe,
    GetMskUtcPipe,
    LocalDatePickerComponent,
    LocalToTimezonePipe,
    UtcToTimezonePipe,
    LocalMoscowTimezoneComponent,
    DropdownDirective,
    StkTitlePipe,
  ],
  providers: [
    { provide: DateTimeAdapter, useClass: PickDatetimeDateAdapter },
    { provide: OWL_DATE_TIME_LOCALE, useValue: 'ru-RU' },
    { provide: OWL_DATE_TIME_FORMATS, useValue: PICK_DATETIME_CUSTOM_DATE_FORMAT },
    { provide: BUTTONS, multi: true, useValue: CUSTOM_LIBRARY_BUTTONS },
    { provide: NZ_I18N, useValue: CUSTOM_LANGUAGE_CONFIG },
  ],
})
export class SharedModule {
  constructor(private translocoService: TranslocoService) {
    registerTranslationsManually('sharedModule', this.translocoService, import.meta.webpackContext!('./translation'));
  }
}
